// Q&A Modal Component
// Modal for creating and editing Q&A items
// Supports both create and edit modes with tag and link management

import React, { useState, useEffect } from 'react';
import { RevoltModal } from '../../../../components/RevoltModal';
import { RevoltInput } from '../../../../components/RevoltInput';
import { RevoltTextarea } from '../../../../components/RevoltTextarea';
import { RevoltButton } from '../../../../components/RevoltButton';
import './QAModal.css';

export function QAModal({
  isOpen,
  onClose,
  mode = 'create', // 'create' or 'edit'
  initialData = {},
  defaultCategoryId = '', // Default category for create mode
  onSubmit
}) {
  // Form state
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [tags, setTags] = useState([]);
  const [links, setLinks] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Link management state
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkType, setNewLinkType] = useState('internal');


  // Initialize form data
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setQuestion(initialData.question || '');
        setAnswer(initialData.answer || '');
        setTags([...(initialData.tags || [])]);
        setLinks([...(initialData.links || [])]);
      } else {
        // Create mode
        setQuestion('');
        setAnswer('');
        setTags([]);
        setLinks([]);
      }
      setNewTag('');
      setNewLinkTitle('');
      setNewLinkUrl('');
      setNewLinkType('direct');
      setIsSubmitting(false);
    }
  }, [isOpen, mode, initialData]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!question.trim() || !answer.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const qaData = {
        question: question.trim(),
        answer: answer.trim(),
        tags,
        links
      };

      await onSubmit(qaData);
      onClose();
    } catch (error) {
      console.error('Failed to submit Q&A:', error);
      alert('Failed to save Q&A. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tag management
  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Link management
  const addLink = () => {
    if (newLinkTitle.trim() && newLinkUrl.trim()) {
      setLinks([...links, {
        title: newLinkTitle.trim(),
        url: newLinkUrl.trim(),
        type: newLinkType
      }]);
      setNewLinkTitle('');
      setNewLinkUrl('');
      setNewLinkType('internal');
    }
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleLinkKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLink();
    }
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Create Q&A' : 'Edit Q&A'}
      size="large"
      containerClassName="qa-modal-container"
    >
      <form onSubmit={handleSubmit} className="qa-modal-form">
        {/* Question */}
        <div className="form-group">
          <RevoltInput
            label="Question *"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Enter the question..."
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Answer */}
        <div className="form-group">
          <RevoltTextarea
            label="Answer *"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Enter the detailed answer..."
            rows={6}
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Tags */}
        <div className="form-group">
          <label className="form-label">Tags</label>
          <div className="tags-input-container">
            <RevoltInput
              value={newTag}
              onChange={(e) => setNewTag(e.target.value.replace(/\s+/g, '-'))}
              onKeyPress={handleTagKeyPress}
              placeholder="Add a tag..."
              disabled={isSubmitting}
            />
            <RevoltButton
              type="button"
              variant="secondary"
              size="small"
              onClick={addTag}
              disabled={isSubmitting || !newTag.trim()}
            >
              Add Tag
            </RevoltButton>
          </div>
          {tags.length > 0 && (
            <div className="tags-list">
              {tags.map((tag, index) => (
                <span key={index} className="tag-item">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="tag-remove"
                    disabled={isSubmitting}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Links */}
        <div className="form-group">
          <label className="form-label">Links</label>
          <div className="links-input-container">
            <RevoltInput
              value={newLinkTitle}
              onChange={(e) => setNewLinkTitle(e.target.value)}
              placeholder="Link title..."
              disabled={isSubmitting}
            />
            <RevoltInput
              value={newLinkUrl}
              onChange={(e) => setNewLinkUrl(e.target.value)}
              onKeyPress={handleLinkKeyPress}
              placeholder="URL..."
              disabled={isSubmitting}
            />
            <select
              value={newLinkType}
              onChange={(e) => setNewLinkType(e.target.value)}
              className="link-type-select"
              disabled={isSubmitting}
            >
              <option value="direct">Direct</option>
              <option value="relative">Relative</option>
            </select>
            <RevoltButton
              type="button"
              variant="secondary"
              size="small"
              onClick={addLink}
              disabled={isSubmitting || !newLinkTitle.trim() || !newLinkUrl.trim()}
            >
              Add Link
            </RevoltButton>
          </div>

          {/* Inline link syntax explanation */}
          <div className="link-syntax-help">
            <small>
              <strong>Tip:</strong> Use <code>[display text]{'{link name}'}</code> in your answer to create clickable inline links.
              Links used inline won't appear in the Related Links section below.
            </small>
          </div>

          {links.length > 0 && (
            <div className="links-list">
              {links.map((link, index) => (
                <div key={index} className="link-item">
                  <div className="link-info">
                    <strong>{link.title}</strong>
                    <span className={`link-type link-type-${link.type}`}>
                      {link.type}
                    </span>
                  </div>
                  <div className="link-url">{link.url}</div>
                  <button
                    type="button"
                    onClick={() => removeLink(index)}
                    className="link-remove"
                    disabled={isSubmitting}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <RevoltButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>
          <RevoltButton
            type="submit"
            variant="primary"
            disabled={isSubmitting || !question.trim() || !answer.trim()}
          >
            {isSubmitting ? 'Saving...' : (mode === 'create' ? 'Create Q&A' : 'Update Q&A')}
          </RevoltButton>
        </div>
      </form>
    </RevoltModal>
  );
}